import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import StudentInfoCard from "../components/StudentInfoCard";
import UnitCards from "../components/UnitCards";
import FinancialCards from "../components/FinancialCards";
import FeesChart from "../components/FeesChart";
import axios from "axios";
import "../assets/css/studentdashboard.css";

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
  ? "https://schools.luth.gov.ng"
  : "http://localhost:3003";


const LoadingSpinner = () => (
  <div className="spinner-container">
    <div className="loading-spinner"></div>
  </div>
);

export default function StudentsDashboard() {
  const [student, setStudent] = useState(null);
  const [feeAmount, setFeeAmount] = useState(0);
  const [registeredUnits, setRegisteredUnits] = useState(0);
  const [maxUnits, setMaxUnits] = useState(0);
  const [payments, setPayments] = useState([]);
  const [results, setResults] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState("payments");
  const [filterLevel, setFilterLevel] = useState("All");
  const [filterSession, setFilterSession] = useState("All");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const levels = Array.from(new Set(results.map((r) => r.Level)));
  const sessions = Array.from(new Set(results.map((r) => r.Session)));

  const getCurrentAcademicSession = () => {
    const year = new Date().getFullYear();
    return `${year}/${year + 1}`;
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const token = localStorage.getItem("token");
        const matricNo = localStorage.getItem("matricNo");
        const headers = { Authorization: `Bearer ${token}` };

        if (!matricNo) {
          setError("Matriculation number not found. Please log in again.");
          setLoading(false);
          return;
        }

        // Get user first
        const userRes = await axios.get(
          `${API_BASE_URL}/api/users/${matricNo}`,
          { headers },
        );

        // Try biodata, but do not block dashboard if it is missing
        let biodata = null;
        try {
          const bioRes = await axios.get(
            `${API_BASE_URL}/api/biodata/${matricNo}`,
            { headers },
          );
          biodata = bioRes.data;
        } catch (bioErr) {
          biodata = null;
        }

        const currentAcademicSession = getCurrentAcademicSession();

        const level = biodata?.level || localStorage.getItem("level") || "";
        const session =
          biodata?.session || localStorage.getItem("session") || "";

        setStudent({
          photo: biodata?.picture || "",
          name: userRes.data.fullName || "Student",
          matricNo: userRes.data.matricNo || matricNo,
          regNo: biodata?.jambRegNumber || "N/A",
          faculty: userRes.data.role === "student" ? "Health Sciences" : "",
          department: userRes.data.programme || "",
          programme: userRes.data.programme || "",
          level: level || "N/A",
          session: session || "N/A",
          academicSession: currentAcademicSession,
          entryMode: biodata?.entryMode || "UTME",
          entryYear: biodata?.entryYear || "N/A",
        });

        // Only fetch fee/course data if we have usable level/session
        if (level && session) {
          const [
            { data: feeData },
            { data: regCourses },
            { data: maxUnitsData },
          ] = await Promise.all([
            axios.get(`${API_BASE_URL}/api/fees`, {
              headers,
              params: {
                programmeName: userRes.data.programme,
                level,
                semester: session,
              },
            }),
            axios.get(`${API_BASE_URL}/api/courses/registered`, {
              headers,
              params: {
                matricNo,
                level,
                session,
                academicSession: currentAcademicSession,
              },
            }),
            axios.get(`${API_BASE_URL}/api/courses/maxUnits`, {
              headers,
              params: {
                programmeName: userRes.data.programme,
                level,
                semester: session,
              },
            }),
          ]);

          setFeeAmount(Number(feeData?.feeAmount || 0));
          setRegisteredUnits(
            Array.isArray(regCourses)
              ? regCourses.reduce(
                  (sum, c) => sum + Number(c.CreditUnits || 0),
                  0,
                )
              : 0,
          );
          setMaxUnits(Number(maxUnitsData?.totalUnits || 0));
        } else {
          setFeeAmount(0);
          setRegisteredUnits(0);
          setMaxUnits(0);
        }

        const { data: payHist } = await axios.get(
          `${API_BASE_URL}/api/payments/history`,
          { headers, params: { matricNo } },
        );
        setPayments(Array.isArray(payHist) ? payHist : []);

        const { data: gradeData } = await axios.get(
          `${API_BASE_URL}/api/results/view`,
          { headers, params: { matricNo } },
        );
        setResults(Array.isArray(gradeData) ? gradeData : []);

        const { data: noteData } = await axios.get(
          `${API_BASE_URL}/api/notifications`,
          { headers, params: { matricNo } },
        );
        setNotifications(Array.isArray(noteData) ? noteData : []);
      } catch (e) {
        console.error("Dashboard load error:", e);
        setError("Could not load your dashboard data.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="error-message">{error}</p>;
  if (!student)
    return <p className="error-message">Student data unavailable.</p>;

  const totalFees = feeAmount || 0;
  const totalPaymentsVal = totalFees * 0.6;
  const totalOutstanding = totalFees - totalPaymentsVal;
  const unusedUnits = maxUnits - registeredUnits;

  return (
    <div className="student-dashboard" style={{ marginTop: "-130px" }}>
      <Sidebar />

      <div className="main-content">
        <div className="top-section">
          <StudentInfoCard student={student} />
          <div className="right-section">
            <UnitCards
              maxUnits={maxUnits}
              registeredUnits={registeredUnits}
              unusedUnits={unusedUnits}
            />
            <div className="financial-section">
              <FinancialCards
                totalFees={totalFees}
                totalPayments={totalPaymentsVal}
                totalOutstanding={totalOutstanding}
              />
              <FeesChart
                programmeName={student.programme}
                level={student.level}
                semester={student.session}
              />
            </div>
          </div>
        </div>

        {/* rest of your component stays the same */}
      </div>
    </div>
  );
}
